import { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Chip, Popover, Button } from '@mui/material';
import { Text } from '@qwickapps/react-framework';
import { useNavigate } from 'react-router-dom';
import type { Item } from '../../hooks/useProjectsApi';

interface DependencyNode {
  id: string;
  title: string;
  status: Item['status'];
  kind: Item['kind'];
  owner: string | null;
  x: number;
  y: number;
  vx: number;
  vy: number;
  fx?: number;
  fy?: number;
}

interface DependencyEdge {
  source: string;
  target: string;
  isCritical: boolean;
}

interface DependencyGraphProps {
  items: Item[];
  height?: number;
}

const STATUS_COLORS: Record<Item['status'], string> = {
  open: '#9e9e9e',
  in_progress: '#2196f3',
  blocked: '#f44336',
  done: '#4caf50',
  cancelled: '#757575',
};

const KIND_ICONS: Record<Item['kind'], string> = {
  issue: 'bug_report',
  pr: 'merge_type',
  task: 'task',
  blocker: 'warning',
  ci_failure: 'build',
};

function simulateForceLayout(
  nodes: DependencyNode[],
  edges: DependencyEdge[],
  width: number,
  height: number,
  iterations = 100
): DependencyNode[] {
  if (nodes.length === 0) return nodes;

  const centerX = width / 2;
  const centerY = height / 2;

  nodes.forEach((node) => {
    if (node.fx === undefined) {
      node.x = centerX + (Math.random() - 0.5) * 200;
      node.y = centerY + (Math.random() - 0.5) * 200;
    }
    node.vx = 0;
    node.vy = 0;
  });

  const nodeMap = new Map(nodes.map((n) => [n.id, n]));

  for (let iter = 0; iter < iterations; iter++) {
    const alpha = 1 - iter / iterations;

    nodes.forEach((node) => {
      let fx = 0;
      let fy = 0;

      // Repulsion between nodes
      nodes.forEach((other) => {
        if (node.id === other.id) return;
        const dx = node.x - other.x;
        const dy = node.y - other.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const force = 5000 / (dist * dist);
        fx += (dx / dist) * force;
        fy += (dy / dist) * force;
      });

      // Attraction along edges
      edges.forEach((edge) => {
        const source = nodeMap.get(edge.source);
        const target = nodeMap.get(edge.target);
        if (!source || !target) return;

        if (source.id === node.id || target.id === node.id) {
          const other = source.id === node.id ? target : source;
          const dx = other.x - node.x;
          const dy = other.y - node.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const force = (dist - 100) * 0.1 * alpha;
          fx += (dx / dist) * force;
          fy += (dy / dist) * force;
        }
      });

      // Center gravity
      fx += (centerX - node.x) * 0.01 * alpha;
      fy += (centerY - node.y) * 0.01 * alpha;

      node.vx += fx * alpha;
      node.vy += fy * alpha;
      node.vx *= 0.9;
      node.vy *= 0.9;
    });

    nodes.forEach((node) => {
      if (node.fx === undefined) {
        node.x += node.vx;
        node.y += node.vy;
        node.x = Math.max(50, Math.min(width - 50, node.x));
        node.y = Math.max(50, Math.min(height - 50, node.y));
      }
    });
  }

  return nodes;
}

function findCriticalPath(nodes: DependencyNode[], edges: DependencyEdge[]): Set<string> {
  const criticalNodes = new Set<string>();
  const adjList = new Map<string, string[]>();

  nodes.forEach((n) => adjList.set(n.id, []));
  edges.forEach((e) => {
    adjList.get(e.source)?.push(e.target);
  });

  function dfs(nodeId: string, visited: Set<string>, path: string[]): void {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);
    path.push(nodeId);

    const neighbors = adjList.get(nodeId) || [];
    if (neighbors.length === 0) {
      if (path.length > criticalNodes.size) {
        criticalNodes.clear();
        path.forEach((id) => criticalNodes.add(id));
      }
    } else {
      neighbors.forEach((neighbor) => dfs(neighbor, visited, path));
    }

    path.pop();
  }

  nodes.forEach((n) => {
    if (n.status === 'done' || n.status === 'in_progress') {
      dfs(n.id, new Set(), []);
    }
  });

  return criticalNodes;
}

export default function DependencyGraph({ items, height = 400 }: DependencyGraphProps) {
  const navigate = useNavigate();
  const [dimensions, setDimensions] = useState({ width: 800, height });
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
  const [selectedNode, setSelectedNode] = useState<DependencyNode | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [popoverPos, setPopoverPos] = useState({ x: 0, y: 0 });

  // Build edges from items (in a real app, this would come from a dependencies table)
  // For now, we infer some relationships from item kinds
  const edges = useMemo<DependencyEdge[]>(() => {
    const result: DependencyEdge[] = [];
    const blockedItems = items.filter((i) => i.status === 'blocked');
    const inProgressItems = items.filter((i) => i.status === 'in_progress');

    // Items that are blocked depend on in-progress items
    blockedItems.forEach((blocked) => {
      inProgressItems.slice(0, 2).forEach((inProg) => {
        result.push({
          source: blocked.id,
          target: inProg.id,
          isCritical: false,
        });
      });
    });

    // Sequential order from task to done
    const taskOrder: Record<Item['status'], number> = {
      open: 0,
      in_progress: 1,
      blocked: 2,
      done: 3,
      cancelled: -1,
    };

    items.forEach((item, i) => {
      items.slice(i + 1).forEach((other) => {
        if (taskOrder[item.status] < taskOrder[other.status] && item.status !== 'cancelled' && other.status !== 'cancelled') {
          // Only add edge if not already added
          if (!result.some((e) => e.source === item.id && e.target === other.id)) {
            result.push({
              source: item.id,
              target: other.id,
              isCritical: false,
            });
          }
        }
      });
    });

    return result;
  }, [items]);

  // Create nodes
  const nodes = useMemo<DependencyNode[]>(() => {
    return items.map((item) => ({
      id: item.id,
      title: item.title,
      status: item.status,
      kind: item.kind,
      owner: item.owner,
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
    }));
  }, [items]);

  // Calculate critical path
  const criticalPath = useMemo(() => {
    const nodeList = nodes.map((n) => n);
    return findCriticalPath(nodeList, edges);
  }, [nodes, edges]);

  // Layout nodes
  const layoutNodes = useMemo(() => {
    return simulateForceLayout(nodes, edges, dimensions.width, dimensions.height, 50);
  }, [nodes, edges, dimensions]);

  // Update critical path edge status
  const finalEdges = useMemo(() => {
    return edges.map((e) => ({
      ...e,
      isCritical: criticalPath.has(e.source) && criticalPath.has(e.target),
    }));
  }, [edges, criticalPath]);

  useEffect(() => {
    if (containerRef) {
      const rect = containerRef.getBoundingClientRect();
      setDimensions({ width: rect.width, height });
    }
  }, [containerRef, height]);

  const handleNodeClick = useCallback(
    (node: DependencyNode, event: React.MouseEvent<HTMLDivElement>) => {
      setSelectedNode(node);
      setAnchorEl(event.currentTarget);
      setPopoverPos({ x: event.clientX, y: event.clientY });
    },
    []
  );

  const handleClosePopover = useCallback(() => {
    setSelectedNode(null);
    setAnchorEl(null);
  }, []);

  const handleViewInKanban = useCallback(() => {
    navigate('/dashboard/goals');
    handleClosePopover();
  }, [navigate, handleClosePopover]);

  if (items.length === 0) {
    return (
      <Box
        sx={{
          p: 3,
          borderRadius: 2,
          bgcolor: 'var(--theme-surface)',
          border: '1px solid var(--theme-border)',
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text variant="body2" content="No items to display" customColor="var(--theme-text-secondary)" />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: 'var(--theme-surface)',
        border: '1px solid var(--theme-border)',
        position: 'relative',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Text variant="h6" content="Dependency Graph" />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip size="small" icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#9e9e9e' }} />} label="Open" />
          <Chip size="small" icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2196f3' }} />} label="In Progress" />
          <Chip size="small" icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#f44336' }} />} label="Blocked" />
          <Chip size="small" icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4caf50' }} />} label="Done" />
        </Box>
      </Box>

      <Box
        ref={setContainerRef}
        sx={{
          position: 'relative',
          height,
          bgcolor: 'var(--theme-surface-hover)',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <svg width={dimensions.width} height={dimensions.height} style={{ position: 'absolute', top: 0, left: 0 }}>
          <defs>
            <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" fill="#9e9e9e" />
            </marker>
            <marker id="arrowhead-critical" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" fill="#2196f3" />
            </marker>
          </defs>

          {finalEdges.map((edge, i) => {
            const source = layoutNodes.find((n) => n.id === edge.source);
            const target = layoutNodes.find((n) => n.id === edge.target);
            if (!source || !target) return null;

            return (
              <line
                key={i}
                x1={source.x}
                y1={source.y}
                x2={target.x}
                y2={target.y}
                stroke={edge.isCritical ? '#2196f3' : '#9e9e9e'}
                strokeWidth={edge.isCritical ? 2 : 1}
                strokeDasharray={edge.isCritical ? 'none' : '4,4'}
                markerEnd={edge.isCritical ? 'url(#arrowhead-critical)' : 'url(#arrowhead)'}
                opacity={0.6}
              />
            );
          })}
        </svg>

        {layoutNodes.map((node) => (
          <Box
            key={node.id}
            onClick={(e) => handleNodeClick(node, e)}
            sx={{
              position: 'absolute',
              left: node.x - 40,
              top: node.y - 20,
              width: 80,
              p: 1,
              bgcolor: `${STATUS_COLORS[node.status]}20`,
              border: `2px solid ${STATUS_COLORS[node.status]}`,
              borderRadius: 1,
              cursor: 'pointer',
              transition: 'transform 0.1s, box-shadow 0.1s',
              '&:hover': {
                transform: 'scale(1.1)',
                boxShadow: `0 4px 12px ${STATUS_COLORS[node.status]}40`,
                zIndex: 10,
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <span className="material-icons" style={{ fontSize: 12, color: STATUS_COLORS[node.status] }}>
                {KIND_ICONS[node.kind]}
              </span>
              {node.status === 'blocked' && (
                <span className="material-icons" style={{ fontSize: 10, color: '#f44336' }}>
                  flash_on
                </span>
              )}
            </Box>
            <Typography
              variant="caption"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                fontSize: 10,
                lineHeight: 1.2,
                color: 'var(--theme-text)',
                textAlign: 'center',
              }}
            >
              {node.title}
            </Typography>
            {node.owner && (
              <Typography variant="caption" sx={{ fontSize: 8, color: 'var(--theme-text-secondary)', display: 'block', textAlign: 'center' }}>
                {node.owner}
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorReference="anchorPosition"
        anchorPosition={{ top: popoverPos.y, left: popoverPos.x }}
        PaperProps={{
          sx: { p: 2, minWidth: 250 },
        }}
      >
        {selectedNode && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <span className="material-icons" style={{ fontSize: 20, color: STATUS_COLORS[selectedNode.status] }}>
                {KIND_ICONS[selectedNode.kind]}
              </span>
              <Text variant="subtitle1" content={selectedNode.title} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Chip
                size="small"
                label={selectedNode.status.replace(/_/g, ' ')}
                sx={{ bgcolor: `${STATUS_COLORS[selectedNode.status]}20`, color: STATUS_COLORS[selectedNode.status] }}
              />
              {criticalPath.has(selectedNode.id) && (
                <Chip size="small" label="Critical Path" sx={{ bgcolor: '#2196f320', color: '#2196f3' }} />
              )}
            </Box>
            {selectedNode.owner && (
              <Text variant="body2" content={`Owner: ${selectedNode.owner}`} customColor="var(--theme-text-secondary)" sx={{ mb: 1 }} />
            )}
            <Button variant="outlined" size="small" onClick={handleViewInKanban} fullWidth>
              View in Kanban
            </Button>
          </Box>
        )}
      </Popover>
    </Box>
  );
}
